import {
    Scene,
    PerspectiveCamera,
    WebGLRenderer,
    RepeatWrapping,
    TextureLoader,
    MirroredRepeatWrapping,
    Box3,
    Vector3,
    Mesh,
    Vector2,
    LatheGeometry,
    BackSide,
    MeshBasicMaterial,
    MeshStandardMaterial,
    Raycaster,
    CanvasTexture,
    // LinearMipMapNearestFilter,
    // MeshPhysicalMaterial,
} from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { getLights } from "objects/lights";
import { getMaterial, getMirrors } from "./materials";
import {
    top,
    right,
    bottom,
    left,
    topLeft,
    topRigth,
    bottomLeft,
    bottomRight,
    molding,
    cabinet,
    wall,
    glassT,
    marble,
    env,
} from "textures";

export const initialScene = (canvas) => {
    console.log("init");
    const scene = new Scene();
    const camera = new PerspectiveCamera(45, canvas.offsetWidth / canvas.offsetHeight, 0.1, 1000);

    const renderer = new WebGLRenderer({ antialias: true, canvas });
    renderer.shadowMap.enabled = true;
    renderer.setSize(canvas.offsetWidth, canvas.offsetHeight);
    // renderer.setPixelRatio(2);

    const controls = new OrbitControls(camera, canvas);
    //controls.listenToKeyEvents(window);
    // controls.keys = { LEFT: 37, UP: "KeyW", RIGHT: "KeyD", BOTTOM: "KeyS" };
    // controls.keyPanSpeed = 20;
    controls.addEventListener("change", () => {
        // console.log("change", controls);
        // console.log("camera", camera.position);
        renderer.render(scene, camera);
    });
    controls.update();
    console.log("control added");

    // function animate() {
    //     requestAnimationFrame(animate);
    //     // const { x, y, z } = camera.position;
    //     // controls.target.set(x, y, z);
    //     // console.log("controls.target: ", controls.target);
    //     controls.update();
    //     renderer.render(scene, camera);
    // }

    // animate();

    return { scene, camera, renderer };
};

export const getMeshParams = (mesh, scale = 1) => {
    const box = new Box3().setFromObject(mesh);
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);
    // console.log("center: ", center);

    const normal = new Vector3();
    mesh.geometry.computeVertexNormals();
    normal.fromBufferAttribute(mesh.geometry.getAttribute("normal"), 0);

    // console.log("normal: ", normal);
    const rotate = [
        (Math.PI / 2) * Math.round(normal.x),
        (Math.PI / 2) * Math.round(normal.y),
        (Math.PI / 2) * Math.round(normal.z),
    ];

    // const position = [center.x * scale, center.z * scale, center.y * scale];
    const position = [center.x * scale, center.y * scale, center.z * scale];
    const width = Math.max(size.x, size.y) * scale;
    const height = size.z * scale;

    return { rotate, position, width, height };
};

const chooseMaterial = (mesh, types) => {
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (types.metal.includes(mesh.name)) {
        mesh.receiveShadow = false;
        const material = getMaterial("metal", "rgb(229,229,229)");
        return material;
    }
    if (mesh.name === "Drain_Grate") {
        return getMaterial("metal", "rgb(40, 40, 43)");
    }
    if (types.wall.includes(mesh.name)) {
        return getMaterial("wall", "rgb(100,100,100)");
    }

    if (types.plastic.includes(mesh.name)) return getMaterial("plastic", "rgb(255,255,255)");

    if (types.stone.includes(mesh.name)) {
        const material = getMaterial("marble", "rgb(128,128,128)");
        const texture = new TextureLoader().load(marble);
        material.map = texture;

        return material;
    }
    if (types.wood.includes(mesh.name)) {
        const material = getMaterial("wood", "rgb(71, 49, 41)");
        const texture = new TextureLoader().load(molding);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.wood2.includes(mesh.name)) {
        const material = getMaterial("wood", "rgb(169, 151, 130)");
        const texture = new TextureLoader().load(cabinet);
        texture.wrapS = RepeatWrapping;
        texture.wrapT = RepeatWrapping;
        material.map = texture;
        return material;
    }
    if (mesh.name === "Ceiling" || mesh.name === "Ceiling_1") {
        const material = getMaterial("wall", "rgb(255, 255, 255)");
        const texture = new TextureLoader().load(wall);
        texture.wrapS = MirroredRepeatWrapping;
        texture.wrapT = MirroredRepeatWrapping;
        material.map = texture;
        return material;
    }
    if (types.glass.includes(mesh.name)) {
        mesh.castShadow = false;
        return getMaterial("glass", "rgb(255, 255, 255)", 0.5);
    }
    if (types.window.includes(mesh.name)) {
        mesh.castShadow = false;
        return getMaterial("glass", "rgb(255, 255, 255)", 0.2);
    }
    if (types.ceramic.includes(mesh.name)) {
        // mesh.receiveShadow = false;
        return getMaterial("ceramic", "rgb(255, 255, 255)");
    }
    if (mesh.name === "Mesh032") {
        const material = getMaterial("frostedGlass", "rgb(150, 150, 150)");
        const texture = new TextureLoader().load(glassT);
        material.map = texture;
        mesh.receiveShadow = false;
        return material;
    }

    if (types.notReceiveShadow.includes(mesh.name)) {
        mesh.receiveShadow = false;
    }

    if (mesh.name === "Drain") {
        return getMaterial("plastic", "rgb(30,30,30)");
    }

    return mesh.material;
};

const setMaterial = (root, types, meshes = [], mirrors = []) => {
    if (root.children.length > 0) {
        root.children.forEach((child) => {
            if (types.mirror.includes(child.name)) {
                // console.log("child.name: ", child.name);
                mirrors.push(child);
            } else setMaterial(child, types, meshes, mirrors);
        });
        root.remove(...mirrors);
    } else {
        root.material = chooseMaterial(root, types);
        meshes.push(root);
    }

    return { meshes, mirrors };
};

const getEnvironment = (pos) => {
    const points = [];
    for (let i = 0; i < 2; ++i) {
        points.push(new Vector2(20, i * 25));
    }
    const segments = 20;

    const phiStart = Math.PI * 0.15;

    const phiLength = Math.PI * 1.7;

    const environment = new TextureLoader().load(env);

    const geometry = new LatheGeometry(points, segments, phiStart, phiLength);
    const material = new MeshBasicMaterial({
        map: environment,
        side: BackSide,
        // color: "rgb(175, 175, 175)",
        color: "rgb(150, 150, 150)",
        // color: "rgb(255, 255, 255)",
        // color: "rgb(0, 0, 0)",
    });
    const mesh = new Mesh(geometry, material);
    mesh.position.set(...pos);
    if (pos[1] === -13) mesh.rotation.set(Math.PI, 0, Math.PI);
    else mesh.rotation.set(Math.PI, Math.PI * 1.25, Math.PI);

    return mesh;
};

export const initialModels = async (scene, camera, model, settings) => {
    // console.log(dumpObject(root));

    const glft = await new GLTFLoader().loadAsync(model);
    const root = glft.scene;
    const { cameraPos, envPos } = settings;
    camera.position.set(...cameraPos);
    camera.lookAt(0, -1.5, 0);
    const [lights, targets] = getLights(settings.lights, scene);
    // console.log("lights: ", lights.length);
    // console.log("targets: ", targets.length);
    scene.add(...lights, ...targets);

    const { meshes, mirrors } = setMaterial(root, settings.typeMeshes);
    const mirrorList = getMirrors(mirrors, settings.mirrorType);
    scene.add(...mirrorList);

    const environment = getEnvironment(envPos);
    scene.add(environment);

    return { root, meshes };
};

export const getMesh = (position, scene, camera) => {
    const raycaster = new Raycaster();
    raycaster.setFromCamera(position, camera);
    const intersectedObjects = raycaster.intersectObjects(scene.children);
    if (intersectedObjects.length) return intersectedObjects[0].object;
    return null;
};

// ==== NormalMap ====

const createNormalMap = (width, height, gap, tileThickness) => {
    const imgTop = new Image();
    const imgLeft = new Image();
    const imgBottom = new Image();
    const imgRight = new Image();
    const imgTopRight = new Image();
    const imgTopLeft = new Image();
    const imgBottomLeft = new Image();
    const imgBottomRight = new Image();
    imgTop.src = top;
    imgBottom.src = bottom;
    imgRight.src = right;
    imgLeft.src = left;
    imgTopRight.src = topRigth;
    imgTopLeft.src = topLeft;
    imgBottomLeft.src = bottomLeft;
    imgBottomRight.src = bottomRight;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "rgb(128, 127, 255)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
        imgTop,
        gap / 2 + tileThickness,
        gap / 2,
        canvas.width - gap - 2 * tileThickness,
        tileThickness
    );

    ctx.drawImage(
        imgRight,
        canvas.width - gap / 2 - tileThickness,
        gap / 2 + tileThickness,
        tileThickness,
        canvas.height - gap - 2 * tileThickness
    );

    ctx.drawImage(
        imgBottom,
        gap / 2 + tileThickness,
        canvas.height - gap / 2 - tileThickness,
        canvas.width - gap - 2 * tileThickness,
        tileThickness
    );

    ctx.drawImage(
        imgLeft,
        gap / 2,
        gap / 2 + tileThickness,
        tileThickness,
        canvas.height - gap - 2 * tileThickness
    );

    ctx.drawImage(imgTopLeft, gap / 2, gap / 2, tileThickness, tileThickness);
    ctx.drawImage(
        imgTopRight,
        canvas.width - gap / 2 - tileThickness,
        gap / 2,
        tileThickness,
        tileThickness
    );
    ctx.drawImage(
        imgBottomRight,
        canvas.width - gap / 2 - tileThickness,
        canvas.height - gap / 2 - tileThickness,
        tileThickness,
        tileThickness
    );
    ctx.drawImage(
        imgBottomLeft,
        gap / 2,
        canvas.height - gap / 2 - tileThickness,
        tileThickness,
        tileThickness
    );

    const normalMap = new CanvasTexture(canvas);

    return normalMap;
};

// === MAIN FUNCTION ====

export const getMaterials = (settings) => {
    const {
        texture,
        tileWidth,
        tileHeight,
        pos,
        seamColor,
        seamSize,
        material,
        turned,
        normalMap,
    } = settings;

    const width = pos === "horizontal" ? tileWidth : tileHeight;
    const height = pos === "horizontal" ? tileHeight : tileWidth;

    const imageSize = 1024;

    const image = new Image();
    image.src = texture;
    const scale = imageSize / Math.max(width, height);
    const gap = seamSize * scale;
    const vw = Math.min(1, width / height);
    const vh = Math.min(1, height / width);
    // const baseSize = mesh.name === "Drain" ? 193 : 270;
    const baseSize = 270;

    const canvas = document.createElement("canvas");
    canvas.width = imageSize * vw + gap;
    canvas.height = imageSize * vh + gap;

    const ctx = canvas.getContext("2d");

    ctx.translate(0, canvas.height / 2);
    ctx.scale(1, -1);
    ctx.translate(0, -canvas.height / 2);

    ctx.fillStyle = seamColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    let imgWidth = imageSize;
    let imgHeight = imageSize * vh;
    let visibleWidth = canvas.height - gap;
    let visibleHeight = canvas.height - gap;

    if (pos === "vertical") {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(Math.PI / 2);
        ctx.translate(-canvas.height / 2, -canvas.width / 2);

        imgHeight = imageSize * vw;
        visibleWidth = canvas.height - gap;
        visibleHeight = canvas.width - gap;
    }

    if (turned) {
        if (pos === "vertical") {
            ctx.translate(canvas.height / 2, canvas.width / 2);
            ctx.rotate(Math.PI);
            ctx.translate(-canvas.height / 2, -canvas.width / 2);
        } else {
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(Math.PI);
            ctx.translate(-canvas.width / 2, -canvas.height / 2);
        }
    }

    ctx.drawImage(image, 0, 0, imgWidth, imgHeight, gap / 2, gap / 2, visibleWidth, visibleHeight);

    const newTexture = new CanvasTexture(canvas);

    newTexture.repeat.set(baseSize / (width + seamSize), baseSize / (height + seamSize));

    // newTexture.magFilter = LinearMipMapNearestFilter;
    // newTexture.minFilter = LinearMipMapNearestFilter;
    newTexture.wrapS = RepeatWrapping;
    newTexture.wrapT = RepeatWrapping;

    let normalTileMap;

    if (!normalMap) {
        const tileThickness = 27 * Math.min(vw, vh);
        // const tileThickness = 1 * Math.min(vw, vh);

        normalTileMap = createNormalMap(canvas.width, canvas.height, gap, tileThickness);
    } else {
        // const image = new Image();
        // image.src = normalTileMap;
        // const canvas = document.createElement("canvas");
        // canvas.width = imageSize * vw + gap;
        // canvas.height = imageSize * vh + gap;
        // // canvas.width = width + gap;
        // // canvas.height = height + gap;
        // const ctx = canvas.getContext("2d");
        // ctx.drawImage(image, gap / 2, gap / 2, visibleWidth, visibleHeight);

        // ctx.fillStyle = "rgb(128, 128, 255)";
        // ctx.fillRect(0, 0, canvas.width, canvas.height);
        // normalTileMap = new CanvasTexture(canvas);

        normalTileMap = new TextureLoader().load(normalMap);
    }
    normalTileMap.repeat.set(baseSize / (width + seamSize), baseSize / (height + seamSize));

    // normalTileMap.magFilter = LinearMipMapNearestFilter;
    // normalTileMap.minFilter = LinearMipMapNearestFilter;

    normalTileMap.wrapS = RepeatWrapping;
    normalTileMap.wrapT = RepeatWrapping;

    const canvas2 = document.createElement("canvas");
    canvas2.width = canvas.width;
    canvas2.height = canvas.height;

    const ctx2 = canvas2.getContext("2d");

    ctx2.fillStyle = seamColor;
    ctx2.fillRect(0, 0, canvas2.width, gap / 2);
    ctx2.fillRect(0, 0, gap / 2, canvas2.height);
    ctx2.fillRect(canvas2.width - gap / 2, 0, gap / 2, canvas2.height);
    ctx2.fillRect(0, canvas2.height - gap / 2, canvas2.width, gap / 2);

    const groutTexture = new CanvasTexture(canvas2);

    groutTexture.repeat.set(baseSize / (width + seamSize), baseSize / (height + seamSize));

    // groutTexture.magFilter = LinearMipMapNearestFilter;
    // groutTexture.minFilter = LinearMipMapNearestFilter;
    groutTexture.wrapS = RepeatWrapping;
    groutTexture.wrapT = RepeatWrapping;

    const material1 = new MeshStandardMaterial({
        map: groutTexture,
        // clearcoat: 0,
        color: "rgb(128, 128, 128)",
        roughness: 1,
        transparent: true,
    });
    console.log("material1: ", material1);

    const material2 = new MeshStandardMaterial({
        map: newTexture,
        // envMap: newTexture,
        normalMap: normalTileMap,
        color: "rgb(128, 128, 128)",
        // emissiveIntensity: 1,
        // clearcoat: material === "polish" ? 0.5 : 0,
        roughness: material === "polish" ? 0.3 : 1,
        metalness: material === "polish" ? 0.2 : 0,
    });

    return [material1, material2];
};

// function dumpObject(obj, lines = [], isLast = true, prefix = "") {
//     const localPrefix = isLast ? "└─" : "├─";
//     lines.push(
//         `${prefix}${prefix ? localPrefix : ""}${obj.name || "*no-name*"} [${
//             obj.type
//         }]`
//     );
//     const newPrefix = prefix + (isLast ? "  " : "│ ");
//     const lastNdx = obj.children.length - 1;
//     obj.children.forEach((child, ndx) => {
//         const isLast = ndx === lastNdx;
//         dumpObject(child, lines, isLast, newPrefix);
//     });
//     return lines;
// }
